import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";

export default function Register() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [nickname, setNickname] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) {
    navigate("/", { replace: true });
    return null;
  }

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("비밀번호가 일치하지 않습니다.");
      return;
    }

    if (password.length < 6) {
      setError("비밀번호는 6자 이상이어야 합니다.");
      return;
    }

    if (!nickname.trim()) {
      setError("닉네임을 입력해주세요.");
      return;
    }

    setLoading(true);

    try {
      const { error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { nickname: nickname.trim() },
        },
      });

      if (signUpError) {
        setError(signUpError.message);
        return;
      }

      navigate("/", { replace: true });
    } catch {
      setError("회원가입 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <img
              alt="월드컵티비 시즌2"
              className="w-10 h-10 rounded-lg"
              src="https://readdy.ai/api/search-image?query=Modern%20sports%20streaming%20TV%20logo%20design%20for%20World%20Cup%20TV%20brand%2C%20sleek%20minimalist%20icon%20with%20soccer%20ball%20and%20play%20button%20motif%2C%20dark%20theme%20compatible%20with%20vibrant%20accent%20highlights%2C%20professional%20broadcast%20studio%20aesthetic%2C%20clean%20geometric%20shapes%2C%20high%20contrast%20flat%20design%2C%20esports%20sports%20branding%20style&width=512&height=512&seq=worldcuptv-logo-01&orientation=squarish"
            />
            <span className="text-xl font-black tracking-tight">
              <span className="text-primary-500">월</span>
              <span className="text-foreground-700">드컵티비 시즌2</span>
            </span>
          </Link>
          <p className="text-foreground-600 text-sm mt-3">공식 커뮤니티에 가입하고 모든 기능을 이용하세요</p>
        </div>

        <div className="bg-background-100 rounded-xl border border-background-300 p-6">
          <h1 className="text-lg font-bold text-foreground-700 mb-6 text-center">회원가입</h1>

          <form onSubmit={handleRegister} className="space-y-4">
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-sm text-red-400">
                {error}
              </div>
            )}

            <div>
              <label className="block text-xs text-foreground-600 mb-1.5">이메일</label>
              <div className="flex items-center gap-2 bg-background-200 rounded-lg px-4 py-3 border border-background-300 focus-within:border-primary-500/50 transition-colors">
                <span className="w-4 h-4 flex items-center justify-center text-foreground-600">
                  <i className="ri-mail-line" />
                </span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="이메일을 입력하세요"
                  required
                  className="bg-transparent text-sm text-foreground-700 placeholder-foreground-500 w-full outline-none"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs text-foreground-600 mb-1.5">닉네임</label>
              <div className="flex items-center gap-2 bg-background-200 rounded-lg px-4 py-3 border border-background-300 focus-within:border-primary-500/50 transition-colors">
                <span className="w-4 h-4 flex items-center justify-center text-foreground-600">
                  <i className="ri-user-line" />
                </span>
                <input
                  type="text"
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  placeholder="닉네임을 입력하세요"
                  required
                  className="bg-transparent text-sm text-foreground-700 placeholder-foreground-500 w-full outline-none"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs text-foreground-600 mb-1.5">비밀번호</label>
              <div className="flex items-center gap-2 bg-background-200 rounded-lg px-4 py-3 border border-background-300 focus-within:border-primary-500/50 transition-colors">
                <span className="w-4 h-4 flex items-center justify-center text-foreground-600">
                  <i className="ri-lock-line" />
                </span>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="비밀번호를 입력하세요 (6자 이상)"
                  required
                  className="bg-transparent text-sm text-foreground-700 placeholder-foreground-500 w-full outline-none"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs text-foreground-600 mb-1.5">비밀번호 확인</label>
              <div className="flex items-center gap-2 bg-background-200 rounded-lg px-4 py-3 border border-background-300 focus-within:border-primary-500/50 transition-colors">
                <span className="w-4 h-4 flex items-center justify-center text-foreground-600">
                  <i className="ri-lock-line" />
                </span>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="비밀번호를 다시 입력하세요"
                  required
                  className="bg-transparent text-sm text-foreground-700 placeholder-foreground-500 w-full outline-none"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-primary-500 text-foreground-50 text-sm font-bold rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
            >
              {loading ? "가입 중..." : "회원가입"}
            </button>

            {/* honeypot */}
            <div className="hp-field">
              <input
                type="text"
                name="company_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />
            </div>
          </form>

          <div className="mt-6 pt-4 border-t border-background-300 text-center">
            <p className="text-xs text-foreground-600">
              이미 계정이 있으신가요?{" "}
              <Link to="/login" className="text-primary-400 hover:text-primary-300 font-bold">
                로그인
              </Link>
            </p>
          </div>
        </div>

        <div className="text-center mt-4">
          <Link to="/" className="text-xs text-foreground-600 hover:text-foreground-500 transition-colors">
            &larr; 홈으로 돌아가기
          </Link>
        </div>
      </div>
    </div>
  );
}